/**
 * A table's paging, search, sort and filters as router search params.
 *
 * `page` is 1-based in the URL (a shared link reads "page 2") and 0-based in
 * state, as `Pagination` counts. Default values are left out of the URL so an
 * untouched table has a clean address. `prefix` namespaces every key when two
 * tables share a route (`sh_page`, `sh_q`, ...).
 */
export type TableFilterValue = string | string[] | boolean;
export type TableFilters = Record<string, TableFilterValue>;
export type TableSortState = { key: string; order: 'asc' | 'desc' };

export type TableSearchState = {
  page: number;
  q: string;
  sort?: TableSortState;
  filters: TableFilters;
};

type RawSearch = Record<string, unknown>;

const KEYS = ['page', 'q', 'sort', 'order', 'filters'] as const;

/** Values that mean "unset" never reach the URL or the query string. */
export function isEmptyFilter(value: unknown): boolean {
  if (value === undefined || value === null || value === false) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
}

function coerceFilters(raw: unknown): TableFilters {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const out: TableFilters = {};
  for (const [key, value] of Object.entries(raw)) {
    // The router JSON-parses search values, so `7` arrives as a number.
    if (typeof value === 'number') {
      out[key] = String(value);
    } else if (typeof value === 'string' || typeof value === 'boolean') {
      if (!isEmptyFilter(value)) out[key] = value;
    } else if (Array.isArray(value)) {
      const items = value
        .filter((v) => typeof v === 'string' || typeof v === 'number')
        .map(String);
      if (items.length > 0) out[key] = items;
    }
  }
  return out;
}

export function readTableSearch(
  search: RawSearch,
  prefix = '',
): TableSearchState {
  const rawPage = Number(search[`${prefix}page`]);
  const page = Number.isInteger(rawPage) && rawPage >= 1 ? rawPage - 1 : 0;
  const rawQ = search[`${prefix}q`];
  const q =
    typeof rawQ === 'string'
      ? rawQ
      : typeof rawQ === 'number'
        ? String(rawQ)
        : '';
  const sortKey = search[`${prefix}sort`];
  const order = search[`${prefix}order`];
  const sort: TableSortState | undefined =
    typeof sortKey === 'string' && sortKey !== ''
      ? { key: sortKey, order: order === 'asc' ? 'asc' : 'desc' }
      : undefined;
  return {
    page,
    q,
    sort,
    filters: coerceFilters(search[`${prefix}filters`]),
  };
}

/**
 * `search` with this table's keys replaced by `state`; every other key
 * (another table's, a route's own) is carried through untouched.
 */
export function writeTableSearch(
  search: RawSearch,
  state: TableSearchState,
  prefix = '',
): RawSearch {
  const next: RawSearch = { ...search };
  for (const key of KEYS) delete next[`${prefix}${key}`];
  if (state.page > 0) next[`${prefix}page`] = state.page + 1;
  if (state.q !== '') next[`${prefix}q`] = state.q;
  if (state.sort) {
    next[`${prefix}sort`] = state.sort.key;
    next[`${prefix}order`] = state.sort.order;
  }
  const filters = Object.fromEntries(
    Object.entries(state.filters).filter(([, v]) => !isEmptyFilter(v)),
  );
  if (Object.keys(filters).length > 0) next[`${prefix}filters`] = filters;
  return next;
}
